import { useEffect, useMemo, useState } from "react";
import { Quote, RefreshCw } from "lucide-react";
import { quoteForDate, _quotesForTests as allQuotes } from "@/lib/dailyQuote";

/**
 * Motivational-quote card shown on the dashboard header for the currently-
 * selected day. Deterministic per date so every clinician sees the same
 * quote at morning huddle.
 *
 * The shuffle button PERSISTS the chosen quote on the day (via `onShuffle`
 * prop) so Word / Excel / Print exports render the exact quote the user
 * cycled to on-screen. If no persistent day exists yet (fresh date with no
 * census), the component falls back to a local-state shuffle so the user
 * can still preview different quotes on-screen.
 */
export default function DailyQuote({ dateIso, offset = 0, onShuffle }) {
  const [localOffset, setLocalOffset] = useState(0);

  // Reset the local (unsaved) shuffle whenever the date changes so we don't
  // carry an old offset onto a different day.
  useEffect(() => setLocalOffset(0), [dateIso]);

  // Prefer the server-persisted offset. Local offset only comes into play
  // when `onShuffle` hasn't been wired (i.e. no day record yet).
  const effectiveOffset = (offset || 0) + (onShuffle ? 0 : localOffset);

  const quote = useMemo(() => {
    if (!effectiveOffset) return quoteForDate(dateIso);
    const base = quoteForDate(dateIso);
    const baseIdx = allQuotes.findIndex((q) => q.text === base.text);
    return allQuotes[(baseIdx + effectiveOffset + allQuotes.length) % allQuotes.length];
  }, [dateIso, effectiveOffset]);

  const handleShuffle = () => {
    if (onShuffle) {
      onShuffle((offset || 0) + 1);
    } else {
      setLocalOffset((n) => n + 1);
    }
  };

  return (
    <div
      className="mt-4 flex items-start gap-3 rounded-xl border border-[#e2e8f0] bg-gradient-to-br from-[#f7f4ec] to-[#f8fafc] px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
      data-testid="daily-quote"
    >
      <Quote className="w-4 h-4 mt-1 flex-shrink-0 text-[#0284c7]" />
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-[#475569]">
          Quote of the day
        </p>
        <p
          className="mt-1 font-heading text-[15px] leading-snug text-[#0f172a]"
          data-testid="daily-quote-text"
        >
          &ldquo;{quote.text}&rdquo;
        </p>
        <p className="mt-1 text-xs text-[#475569] italic" data-testid="daily-quote-author">
          — {quote.author}
        </p>
      </div>
      <button
        type="button"
        onClick={handleShuffle}
        aria-label="Show another quote"
        title={onShuffle ? "Show another quote (saved for Word / Excel / Print)" : "Show another quote (save the day to persist)"}
        data-testid="daily-quote-shuffle"
        className="flex-shrink-0 p-1.5 rounded-md text-[#475569] hover:text-[#0284c7] hover:bg-white/70 transition-colors"
      >
        <RefreshCw className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
